// The platform console (admin.{base}): every company on eBookMe. Staff pause or resume companies, change their
// address, set a plan by hand (for deals outside Stripe) and extend trials. Billing itself lives in Stripe.
import { useMemo, useState } from "react";
import { Building2, Pause, Play, Search, ShieldCheck } from "lucide-react";
import { api, useApi } from "../lib/api";
import { shortDate } from "../lib/format";
import type { Plan, PlatformCompany } from "../lib/types";
import { Chip, inputCls } from "../components/bits";
import { Button, cx, Empty, ErrorNote, PromptDialog, Spinner, toast } from "../components/ui";

export default function Platform() {
  const { data, error, loading, reload } = useApi<{ companies: PlatformCompany[]; plans: Plan[] }>("/platform/companies");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [pausing, setPausing] = useState<PlatformCompany | null>(null);
  const [renaming, setRenaming] = useState<PlatformCompany | null>(null);
  const [trial, setTrial] = useState<PlatformCompany | null>(null);
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (data?.companies ?? []).filter((c) => !n || c.name.toLowerCase().includes(n) || c.slug.includes(n) || c.createdBy.includes(n) || c.cid.includes(n));
  }, [data, q]);
  const set = async (c: PlatformCompany, body: Record<string, string | number>, ok: string) => {
    setBusy(c.cid);
    try {
      await api(`/platform/companies/${c.cid}`, { body });
      toast(ok);
      await reload();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };
  const companies = data?.companies ?? [];
  const paying = companies.filter((c) => c.billing?.subscriptionId && ["active", "past_due"].includes(c.billing.status ?? ""));
  const arr = paying.reduce((sum, c) => sum + c.paidPlan.pricePerSeatYear * (c.billing.seats ?? 0), 0);
  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <h1 className="text-xl font-semibold flex items-center gap-2 mb-1">
        <ShieldCheck className="size-5" /> Platform
      </h1>
      <p className="text-sm text-muted mb-5">Every company on eBookMe. New companies are active at once on a 14-day Standard trial. You never see their calendars or meetings.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          ["Companies", companies.length],
          ["On trial", companies.filter((c) => c.onTrial).length],
          ["Paying", paying.length],
          ["Yearly revenue", `$${arr.toLocaleString()}`],
        ].map(([label, v]) => (
          <div key={label} className="bg-surface border border-line rounded-xl px-4 py-3">
            <div className="text-xs text-muted">{label}</div>
            <div className="text-2xl font-semibold tabular">{v}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:items-center">
        <div className="relative sm:w-72">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Company, address, owner or id" aria-label="Search" className={cx(inputCls, "pl-9")} />
        </div>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : !list.length ? (
        <Empty icon={<Building2 className="size-10" />} title="No companies yet" />
      ) : (
        <div className="bg-surface border border-line rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted border-b border-line">
              <tr>
                <th className="px-4 py-2 font-medium">Company</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Plan</th>
                <th className="px-4 py-2 font-medium">Billing</th>
                <th className="px-4 py-2 font-medium">People</th>
                <th className="px-4 py-2 font-medium">Emails this month</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.map((c) => (
                <tr key={c.cid}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{c.name}</div>
                    <a href={c.url} target="_blank" rel="noopener" className="text-xs text-accent-text hover:underline whitespace-nowrap">
                      {c.url.replace(/^https?:\/\//, "")}
                    </a>
                    <button className="text-xs text-muted hover:text-ink underline ml-2" onClick={() => setRenaming(c)}>
                      change
                    </button>
                    <div className="text-xs text-muted">
                      {c.createdBy} · registered {shortDate(c.createdAt)} · <span className="font-mono">{c.cid}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    {c.status === "active" ? <Chip tone="good">Active</Chip> : <Chip tone="bad">Paused</Chip>}
                    {c.suspendReason && c.status === "suspended" && (
                      <div className="text-xs text-muted mt-0.5 max-w-48 truncate" title={c.suspendReason}>
                        {c.suspendReason}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <select aria-label={`Plan for ${c.name}`} value={c.paidPlan.id} disabled={busy === c.cid} onChange={(e) => set(c, { plan: e.target.value }, "Plan changed")} className={cx(inputCls, "h-8")} style={{ width: "8rem" }}>
                      {data!.plans.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <div className="text-xs text-muted mt-0.5">
                      {c.onTrial ? `${c.plan.name} trial to ${shortDate(c.trialEndsAt)}` : c.trialEndsAt && Date.parse(c.trialEndsAt) <= Date.now() ? `Trial ended ${shortDate(c.trialEndsAt)}` : ""}{" "}
                      <button className="underline hover:text-ink" onClick={() => setTrial(c)}>
                        trial…
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-ink-2 whitespace-nowrap">
                    {c.billing?.subscriptionId ? (
                      <>
                        <Chip tone={c.billing.status === "active" ? "good" : c.billing.status === "past_due" ? "warn" : "muted"}>{c.billing.status ?? "—"}</Chip>
                        <div className="text-xs text-muted mt-0.5">
                          {c.billing.seats ?? 0} seats · ${(c.paidPlan.pricePerSeatYear * (c.billing.seats ?? 0)).toLocaleString()}/yr
                        </div>
                      </>
                    ) : (
                      <span className="text-muted">None</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 tabular">{c.members}</td>
                  <td className="px-4 py-2.5 text-ink-2 whitespace-nowrap tabular">
                    {c.usage.sent}
                    <div className="text-xs text-muted">
                      {c.usage.bounced} bounced · {c.usage.complained} spam
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1.5">
                      {c.status === "suspended" ? (
                        <Button className="h-8" busy={busy === c.cid} onClick={() => set(c, { status: "active" }, `${c.name} resumed`)}>
                          <Play className="size-3.5" /> Resume
                        </Button>
                      ) : (
                        <Button className="h-8" onClick={() => setPausing(c)}>
                          <Pause className="size-3.5" /> Pause
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {renaming && (
        <PromptDialog
          title={`Change ${renaming.name}'s address`}
          label="New address (existing booking links on the old address stop working)"
          initial={renaming.slug}
          confirm="Change address"
          onClose={() => setRenaming(null)}
          onSubmit={async (slug) => {
            await api(`/platform/companies/${renaming.cid}`, { body: { slug } });
            toast("Address changed");
            await reload();
          }}
        />
      )}
      {trial && (
        <PromptDialog
          title={`Trial for ${trial.name}`}
          label="Days of Standard from today (0 ends the trial now)"
          initial="14"
          confirm="Set trial"
          validate={(v) => (/^\d{1,3}$/.test(v.trim()) && Number(v) <= 365 ? null : "Enter 0–365")}
          onClose={() => setTrial(null)}
          onSubmit={(v) => set(trial, { trialDays: Number(v.trim()) }, "Trial updated")}
        />
      )}
      {pausing && <PromptDialog title={`Pause ${pausing.name}?`} label="Reason (the owner is told)" confirm="Pause company" onClose={() => setPausing(null)} onSubmit={(reason) => set(pausing, { status: "suspended", reason }, `${pausing.name} paused`)} />}
    </div>
  );
}
